/**
 * Copyright (c) 2025-2026 eBUCIS, LLC. All rights reserved.
 *
 * Messages - Full messaging page for all user types
 * Provides inbox view with conversation list and thread display
 */

import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { MessageSquare, Users } from 'lucide-react'
import { useAuthStore } from '../stores/authStore'
import {
  ConversationList,
  ConversationThread,
  NewConversationModal,
  PodInbox
} from '../components/messaging'
import podMessagingService from '../services/podMessagingService'

/**
 * Direct (relationship-anchored 1:1) messages and pod threads are separate systems on the server
 * and stay separate here: a pod thread is org-scoped and visible to the pod's whole tutor team,
 * which is not true of a 1:1 conversation. Two tabs keep that boundary legible.
 */
function MessagesTabs({ activeTab, onChange, hasPods, podUnread }) {
  if (!hasPods) return null
  const tab = (id, label, Icon, badge) => (
    <button
      key={id}
      type="button"
      role="tab"
      aria-selected={activeTab === id}
      onClick={() => onChange(id)}
      className={`inline-flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium ${
        activeTab === id
          ? 'border-indigo-600 text-indigo-700 dark:text-indigo-300'
          : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
      }`}
    >
      <Icon className="h-4 w-4" />
      {label}
      {badge > 0 && (
        <span className="rounded-full bg-indigo-600 px-1.5 py-0.5 text-[11px] text-white">
          {badge > 9 ? '9+' : badge}
        </span>
      )}
    </button>
  )
  return (
    <div
      role="tablist"
      className="flex shrink-0 border-b border-gray-200 bg-white px-2 dark:border-gray-700 dark:bg-gray-900"
    >
      {tab('direct', 'Direct', MessageSquare, 0)}
      {tab('pods', 'Pods', Users, podUnread)}
    </div>
  )
}

const Messages = () => {
  const { conversationId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuthStore()

  const [selectedConversation, setSelectedConversation] = useState(conversationId || null)
  const [showNewModal, setShowNewModal] = useState(false)
  const [refreshTrigger, setRefreshTrigger] = useState(0)
  const [children, setChildren] = useState([]) // For guardians
  const [activeTab, setActiveTab] = useState('direct')
  const [hasPods, setHasPods] = useState(false)
  const [podUnread, setPodUnread] = useState(0)

  // Determine user role for messaging
  const getUserRole = () => {
    if (user?.isCoach) return 'coach'
    if (user?.isGuardian) return 'guardian'
    return 'student'
  }

  // Load children for guardians
  useEffect(() => {
    if (user?.isGuardian) {
      // TODO: Load children from family service
      // For now, this will be empty and can be populated when guardian support is fully integrated
    }
  }, [user])

  // Update URL when conversation changes
  useEffect(() => {
    if (selectedConversation) {
      navigate(`/messages/${selectedConversation}`, { replace: true })
    } else {
      navigate('/messages', { replace: true })
    }
  }, [selectedConversation, navigate])

  // Sync from URL params
  useEffect(() => {
    if (conversationId && conversationId !== selectedConversation) {
      setSelectedConversation(conversationId)
    }
  }, [conversationId])

  const handleSelectConversation = (convId) => {
    setSelectedConversation(convId)
  }

  const handleBack = () => {
    setSelectedConversation(null)
  }

  const handleConversationCreated = (conversation) => {
    setRefreshTrigger((prev) => prev + 1)
    setSelectedConversation(conversation.id)
  }

  const handleConversationUpdated = () => {
    setRefreshTrigger((prev) => prev + 1)
  }

  // The Pods tab is only worth showing to someone who actually has pod threads. Probing once on
  // mount keeps the tab from appearing (and 403-ing) for accounts without the feature.
  useEffect(() => {
    let cancelled = false
    podMessagingService
      .listThreads()
      .then((threads) => {
        if (cancelled) return
        setHasPods(threads.length > 0)
        setPodUnread(threads.reduce((sum, t) => sum + (t.unreadCount || 0), 0))
      })
      .catch(() => {
        if (!cancelled) setHasPods(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  if (activeTab === 'pods') {
    return (
      <div className="flex h-[calc(100dvh-4rem)] flex-col bg-gray-50 dark:bg-gray-950">
        <MessagesTabs
          activeTab={activeTab}
          onChange={setActiveTab}
          hasPods={hasPods}
          podUnread={podUnread}
        />
        <div className="min-h-0 flex-1">
          <PodInbox onUnreadChange={setPodUnread} />
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-[calc(100dvh-4rem)] flex-col bg-gray-50 dark:bg-gray-950">
      <MessagesTabs
        activeTab={activeTab}
        onChange={setActiveTab}
        hasPods={hasPods}
        podUnread={podUnread}
      />
      <div className="flex min-h-0 flex-1">
        {/* Desktop: Side-by-side layout */}
        {/* Mobile: Show list or thread based on selection */}

        {/* Conversation List */}
        <div
          className={`w-full md:w-80 lg:w-96 flex-shrink-0 ${
            selectedConversation ? 'hidden md:flex' : 'flex'
          } flex-col`}
        >
          <ConversationList
            currentUserId={user?.id}
            onSelectConversation={handleSelectConversation}
            selectedConversationId={selectedConversation}
            onNewConversation={() => setShowNewModal(true)}
            refreshTrigger={refreshTrigger}
          />
        </div>

        {/* Conversation Thread */}
        <div className={`flex-1 ${selectedConversation ? 'flex' : 'hidden md:flex'} flex-col`}>
          {selectedConversation ? (
            <ConversationThread
              conversationId={selectedConversation}
              currentUserId={user?.id}
              onBack={handleBack}
              onConversationUpdated={handleConversationUpdated}
            />
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-900 text-gray-500 dark:text-gray-400">
              <MessageSquare className="w-16 h-16 mb-4 text-gray-300 dark:text-gray-600" />
              <h3 className="text-lg font-medium mb-1">Select a conversation</h3>
              <p className="text-sm">Choose from your existing conversations or start a new one</p>
            </div>
          )}
        </div>

        {/* New Conversation Modal */}
        <NewConversationModal
          isOpen={showNewModal}
          onClose={() => setShowNewModal(false)}
          currentUserId={user?.id}
          userRole={getUserRole()}
          children={children}
          onConversationCreated={handleConversationCreated}
        />
      </div>
    </div>
  )
}

export default Messages
